import { useEffect, useState } from "react";
import { supabase, supabaseEnabled } from "@/lib/supabase";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Phone } from "lucide-react";

/**
 * Social + OTP login block. Uses Supabase Auth on the client, then exchanges
 * the Supabase session for our internal JWT via /api/auth/supabase-exchange.
 * Policy: new users MUST provide an invite token (B2B SaaS safety).
 */
export default function SupabaseAuthBlock({ mode = "login" }) {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [invite, setInvite] = useState(
    params.get("invite") || sessionStorage.getItem("viyora_invite_token") || ""
  );
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState("");

  const exchange = async (accessToken) => {
    try {
      const { data } = await api.post("/auth/supabase-exchange", {
        access_token: accessToken,
        invite_token: invite || undefined,
      });
      // Auth token is now an httpOnly cookie set by the backend; we only
      // mirror the user profile for a fast first paint.
      localStorage.setItem("dentalai_user", JSON.stringify(data.user));
      sessionStorage.removeItem("viyora_invite_token");
      toast.success(data.new_user ? "Welcome! Account created." : "Welcome back");
      window.location.href = "/app";
    } catch (err) {
      toast.error(apiError(err, "Exchange failed"));
      if (supabase) await supabase.auth.signOut();
    }
  };

  // On mount: handle any pending Supabase session (e.g. after Google OAuth redirect)
  useEffect(() => {
    if (!supabaseEnabled) return;
    supabase.auth.getSession().then(({ data }) => {
      if (data?.session?.access_token) exchange(data.session.access_token);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_evt, session) => {
      if (session?.access_token) exchange(session.access_token);
    });
    return () => sub?.subscription?.unsubscribe?.();
  }, []); // eslint-disable-line

  if (!supabaseEnabled) return null;

  const signInWithGoogle = async () => {
    setLoading("google");
    // Preserve invite token across the OAuth roundtrip via sessionStorage — the redirectTo URL
    // must be the exact origin registered with the OAuth provider, no path/query allowed.
    if (invite) sessionStorage.setItem("viyora_invite_token", invite);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin },
    });
    if (error) { toast.error(error.message); setLoading(""); }
  };

  const sendOtp = async (e) => {
    e.preventDefault();
    setLoading("otp");
    try {
      const { error } = await supabase.auth.signInWithOtp({ phone });
      if (error) throw error;
      setOtpSent(true);
      toast.success("OTP sent to your phone");
    } catch (err) {
      toast.error(err?.message || "Failed to send OTP");
    } finally { setLoading(""); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setLoading("verify");
    try {
      const { data, error } = await supabase.auth.verifyOtp({ phone, token: otp, type: "sms" });
      if (error) throw error;
      if (data?.session?.access_token) await exchange(data.session.access_token);
    } catch (err) {
      toast.error(err?.message || "OTP verification failed");
    } finally { setLoading(""); }
  };

  return (
    <div className="mt-6" data-testid="supabase-auth-block">
      <div className="flex items-center gap-3 text-xs text-ink-soft my-4">
        <div className="flex-1 h-px bg-[#E8EAE9]" /> OR <div className="flex-1 h-px bg-[#E8EAE9]" />
      </div>

      {mode === "signup" && (
        <input
          data-testid="social-invite-token"
          value={invite}
          onChange={(e) => setInvite(e.target.value)}
          placeholder="Invite token (required for social/OTP signup)"
          className="w-full mb-3 rounded-xl border border-[#E8EAE9] bg-white px-4 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-pine/40"
        />
      )}

      <button
        type="button"
        data-testid="btn-google-login-supabase"
        onClick={signInWithGoogle}
        disabled={loading === "google"}
        className="hidden w-full inline-flex items-center justify-center gap-2 rounded-full border border-[#E8EAE9] bg-white hover:bg-linen px-4 py-2.5 text-sm font-medium transition-all active:scale-[0.98]"
      >
        {loading === "google" ? <Loader2 className="w-4 h-4 animate-spin" /> : (
          <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
        )} Continue with Google
      </button>

      <div className="mt-4 rounded-xl border border-[#E8EAE9] bg-white p-3">
        <div className="flex items-center gap-2 text-xs text-ink-mid mb-2">
          <Phone className="w-3.5 h-3.5" /> Sign in with phone (OTP)
        </div>
        {!otpSent ? (
          <form onSubmit={sendOtp} className="flex gap-2">
            <input data-testid="phone-input" required value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="+91 98xxxxxxxx"
              className="flex-1 rounded-lg border border-[#E8EAE9] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pine/40" />
            <button data-testid="btn-send-otp" disabled={loading === "otp"} className="btn-primary text-xs px-4 py-2">
              {loading === "otp" ? <Loader2 className="w-3 h-3 animate-spin" /> : "Send OTP"}
            </button>
          </form>
        ) : (
          <form onSubmit={verifyOtp} className="flex gap-2">
            <input data-testid="otp-input" required value={otp} onChange={(e) => setOtp(e.target.value)}
              placeholder="6-digit code" inputMode="numeric" maxLength={6}
              className="flex-1 rounded-lg border border-[#E8EAE9] px-3 py-2 text-sm font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-pine/40" />
            <button data-testid="btn-verify-otp" disabled={loading === "verify"} className="btn-primary text-xs px-4 py-2">
              {loading === "verify" ? <Loader2 className="w-3 h-3 animate-spin" /> : "Verify"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
